import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertCircle, FileText, Shield, TrendingUp, Calendar, Settings } from '../ui/icons';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { tenantsApi, documentsApi, certificatesApi, analyticsApi } from '../../services/api';
import { Tenant, Document, Certificate } from '../../services/api';

interface TenantStats {
  totalDocuments: number;
  documentsThisMonth: number;
  acceptanceRate: number;
  pendingDocuments: number;
  certificateStatus: 'active' | 'expiring' | 'expired' | 'none';
}

const TenantDashboard: React.FC = () => {
  const { user } = useAuth();
  const permissions = usePermissions();
  const [tenantInfo, setTenantInfo] = useState<Tenant | null>(null);
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [recentDocuments, setRecentDocuments] = useState<Document[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (permissions.tenantId) {
      loadTenantData();
    }
  }, [permissions.tenantId]);

  const loadTenantData = async () => {
    try {
      setLoading(true);

      // Load tenant information
      if (permissions.tenantId) {
        const tenantResponse = await tenantsApi.getById(permissions.tenantId);
        setTenantInfo(tenantResponse.data);
      }

      // Load documents for this tenant
      const documentsResponse = await documentsApi.getAll({
        tenant_id: permissions.tenantId || undefined,
        page: 1,
        size: 5
      });
      setRecentDocuments(documentsResponse.data.documents);

      // Load certificates
      if (permissions.tenantId) {
        const certsResponse = await certificatesApi.getByTenant(permissions.tenantId);
        setCertificates(certsResponse.data.certificates);
      }

      // Calculate stats
      const allDocsResponse = await documentsApi.getAll({
        tenant_id: permissions.tenantId || undefined
      });
      const allDocs = allDocsResponse.data.documents;

      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const docsThisMonth = allDocs.filter(doc => {
        const docDate = new Date(doc.created_at);
        return docDate.getMonth() === currentMonth && docDate.getFullYear() === currentYear;
      });

      const acceptedDocs = allDocs.filter(doc => doc.status === 'accepted').length;
      const pendingDocs = allDocs.filter(doc => doc.status === 'pending').length;

      let certificateStatus: 'active' | 'expiring' | 'expired' | 'none' = 'none';
      if (certificates.length > 0) {
        const activeCert = certificates.find(cert => cert.status === 'active');
        if (activeCert) {
          const validTo = new Date(activeCert.valid_to);
          const now = new Date();
          const daysToExpiry = Math.ceil((validTo.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

          if (daysToExpiry <= 0) {
            certificateStatus = 'expired';
          } else if (daysToExpiry <= 30) {
            certificateStatus = 'expiring';
          } else {
            certificateStatus = 'active';
          }
        }
      }

      setStats({
        totalDocuments: allDocs.length,
        documentsThisMonth: docsThisMonth.length,
        acceptanceRate: allDocs.length > 0 ? Math.round((acceptedDocs / allDocs.length) * 100) : 0,
        pendingDocuments: pendingDocs,
        certificateStatus
      });

    } catch (error) {
      console.error('Error loading tenant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCertificateStatusBadge = () => {
    if (!stats) return null;

    switch (stats.certificateStatus) {
      case 'active':
        return <Badge variant="default" className="bg-green-100 text-green-800">🔐 Certificado Activo</Badge>;
      case 'expiring':
        return <Badge variant="destructive" className="bg-orange-100 text-orange-800">⚠️ Próximo a Vencer</Badge>;
      case 'expired':
        return <Badge variant="destructive">❌ Certificado Vencido</Badge>;
      case 'none':
        return <Badge variant="secondary">⚪ Sin Certificado</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Cargando información de su empresa...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Company Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl font-bold">
                {tenantInfo?.company_name || 'Mi Empresa'}
              </CardTitle>
              <p className="text-muted-foreground">
                RUC: {tenantInfo?.ruc} • Ambiente: {tenantInfo?.environment === 'beta' ? 'Pruebas' : 'Producción'}
              </p>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <Badge variant={tenantInfo?.status === 'active' ? 'default' : 'secondary'}>
                {tenantInfo?.status === 'active' ? '✅ Activo' : '⏸️ Inactivo'}
              </Badge>
              {getCertificateStatusBadge()}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <FileText className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{stats?.totalDocuments || 0}</p>
                <p className="text-xs text-muted-foreground">Documentos Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <Calendar className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{stats?.documentsThisMonth || 0}</p>
                <p className="text-xs text-muted-foreground">Este Mes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">{stats?.acceptanceRate || 0}%</p>
                <p className="text-xs text-muted-foreground">Tasa de Aceptación</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-8 w-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold">{stats?.pendingDocuments || 0}</p>
                <p className="text-xs text-muted-foreground">Pendientes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Acciones Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button className="h-16 flex flex-col space-y-2">
              <FileText className="h-6 w-6" />
              <span>Crear Documento</span>
            </Button>
            <Button variant="outline" className="h-16 flex flex-col space-y-2">
              <Shield className="h-6 w-6" />
              <span>Subir Certificado</span>
            </Button>
            <Button variant="outline" className="h-16 flex flex-col space-y-2">
              <Settings className="h-6 w-6" />
              <span>Mi Configuración</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Documents */}
      <Card>
        <CardHeader>
          <CardTitle>Documentos Recientes</CardTitle>
        </CardHeader>
        <CardContent>
          {recentDocuments.length > 0 ? (
            <div className="space-y-3">
              {recentDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{doc.document_type} - {doc.document_number}</p>
                    <p className="text-sm text-muted-foreground">
                      Monto: S/ {doc.total_amount} • {new Date(doc.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant={
                    doc.status === 'accepted' ? 'default' :
                    doc.status === 'pending' ? 'secondary' : 'destructive'
                  }>
                    {doc.status === 'accepted' ? 'Aceptado' :
                     doc.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                  </Badge>
                </div>
              ))}
              <Button variant="outline" className="w-full">
                Ver Todos los Documentos
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-2 text-muted-foreground">No hay documentos aún</p>
              <Button className="mt-4">Crear Primer Documento</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantDashboard;